// inv/src/components/ui/lastdeals1.jsx
import { useState, memo } from "react";
import { Calendar } from "lucide-react";
import DealRecipientCompany from "./dealRecipientCompany";

const AmountPill = memo(function AmountPill({ value }) {
  if (!value) return null;
  return (
    <div
      className="inline-flex h-7 items-center justify-center rounded-full bg-white ring-2 ring-gray-200 text-xs font-semibold text-emerald-700 px-2 shadow"
      title={String(value)}
    >
      {value}
    </div>
  );
});

const RoundBadge = memo(function RoundBadge({ value }) {
  if (!value) return null;
  return (
    <div
      className="inline-flex h-7 items-center rounded-full bg-amber-50 text-amber-800 border border-amber-200 px-2 text-[11px] font-semibold shadow whitespace-nowrap"
      title={String(value)}
    >
      {value}
    </div>
  );
});

const IndustryBadge = memo(function IndustryBadge({ value }) {
  if (!value) return null;
  return (
    <div
      className="inline-flex min-h-7 items-center rounded-full bg-stone-100 border border-stone-200 px-2 text-[11px] font-medium text-stone-700 whitespace-normal break-words max-w-full"
      title={String(value)}
    >
      {value}
    </div>
  );
});

const NumberDot = ({ index, topVar }) => (
  <div
    className="absolute left-1/2 -translate-x-1/2 z-10
               inline-flex items-center justify-center
               rounded-full border border-stone-300
               bg-white text-gray-900
               shadow-sm text-[11px] font-semibold leading-none px-1.5"
    style={{
      minWidth: 'var(--dot)',
      height: 'var(--dot)',
      top: topVar,
      boxSizing: 'border-box',
    }}
    aria-label={`Event ${index}`}
  >
    {index}
  </div>
);

export default function Timeline({
  items = [],
  className = "",
  moreText = "Read more",
  lessText = "Read less",
  recipientAccess = "professionalOnly",
  dateCol = 180,
  railCol = 80,
  btnCol = 120,
  gap = 12,
  cardGap = 16,
  dotSize = 20,
  tlXs = 12,
  tlMd = 16,
}) {
  const [open, setOpen] = useState({});
  const toggle = (i) => setOpen((s) => ({ ...s, [i]: !s[i] }));

  const InvestorsSection = ({ title, values }) => {
    if (!values?.length) return null;
    return (
      <div className="mt-3">
        <div className="text-xs font-medium text-gray-500 uppercase tracking-wide">
          {title}
        </div>
        <div className="mt-2 flex flex-wrap gap-2">
          {values.map((node, idx) => (
            <span
              key={idx}
              className="inline-flex items-center rounded-full border px-2.5 py-0.5 text-xs font-medium border-gray-300 text-gray-700"
            >
              {node}
            </span>
          ))}
        </div>
      </div>
    );
  };

  return (
    <div
      className={`relative ${className}`}
      style={{
        ['--date']: `${dateCol}px`,
        ['--rail']: `${railCol}px`,
        ['--btn']: `${btnCol}px`,
        ['--gap']: `${gap}px`,
        ['--tl-xs']: `${tlXs}px`,
        ['--tl-md']: `${tlMd}px`,
        ['--card-gap']: `${cardGap}px`,
        ['--dot']: `${dotSize}px`,
        ['--row-h']: `28px`,
        ['--pad-xs']: `12px`,
        ['--pad-md']: `24px`,
      }}
    >
      <div
        aria-hidden
        className="absolute md:hidden w-0 border-l border-dashed border-stone-300 z-0"
        style={{
          left: 'calc(var(--tl-xs) / 2)',
          top: 'calc(-1 * var(--card-gap) / 2)',
          bottom: 'calc(-1 * var(--card-gap) / 2)',
        }}
      />
      <div
        aria-hidden
        className="absolute hidden md:block w-0 border-l border-dashed border-stone-300 z-0"
        style={{
          left: 'calc(var(--tl-md) / 2)',
          top: 'calc(-1 * var(--card-gap) / 2)',
          bottom: 'calc(-1 * var(--card-gap) / 2)',
        }}
      />

      <ol className="relative z-10 space-y-[var(--card-gap)]">
        {items.map((item, i) => {
          const detailParagraphs = Array.isArray(item.detailParagraphs)
            ? item.detailParagraphs
            : [];
          const hasDetails = Boolean(item.summary || detailParagraphs.length || item.details);

          return (
            <li key={item.id ?? i}>
              <div
                className="md:hidden grid items-start"
                style={{ gridTemplateColumns: 'var(--tl-xs) minmax(0,1fr)', columnGap: 'var(--gap)' }}
              >
                <div className="relative">
                  <NumberDot
                    index={i + 1}
                    topVar="calc(var(--pad-xs) + var(--row-h)/2 - var(--dot)/2)"
                  />
                </div>

                <div className="rounded-md bg-stone-50 p-3 border border-stone-200">
                  <h3 className="min-w-0 break-words text-[17px] leading-7 font-semibold text-gray-900">
                    {item.title}
                  </h3>

                  {(item.hasRecipientCompanyInfo || item.recipientCompany || item.recipientCompanyUrl) && (
                    <div className="mt-2">
                      <DealRecipientCompany
                        companyName={item.recipientCompany}
                        companyUrl={item.recipientCompanyUrl}
                        hasInfo={item.hasRecipientCompanyInfo}
                        accessMode={recipientAccess}
                      />
                    </div>
                  )}

                  <div className="mt-2">
                    {(item.announcementDate || item.date) && (
                      <span className="inline-flex h-7 items-center border-2 border-stone-200 shadow-lg whitespace-nowrap rounded-full bg-stone-900 text-white pl-2 pr-3 text-xs font-semibold">
                        <Calendar aria-hidden className="mr-1.5 h-3.5 w-3.5 opacity-80" />
                        {item.announcementDate || item.date}
                      </span>
                    )}
                    <div className="mt-2">
                      <AmountPill value={item.amount} />
                    </div>
                    {item.round && (
                      <div className="mt-2">
                        <RoundBadge value={item.round} />
                      </div>
                    )}
                    {Array.isArray(item.industries) && item.industries.length > 0 && (
                      <div className="mt-2 flex flex-wrap gap-2">
                        {item.industries.map((ind, idx) => (
                          <IndustryBadge key={idx} value={ind} />
                        ))}
                      </div>
                    )}
                  </div>
                  <InvestorsSection title="Lead investors" values={item.leadInvestors} />
                  <InvestorsSection title="Investors participated" values={item.otherInvestors} />

                  {hasDetails && (
                    <>
                      <div className="mt-3">
                        <button
                          type="button"
                          onClick={() => toggle(i)}
                          className="h-8 w-full whitespace-nowrap cursor-pointer inline-flex items-center justify-center rounded-lg border px-3 text-sm font-medium border-gray-300 text-gray-700 hover:bg-gray-50"
                          aria-expanded={!!open[i]}
                          aria-controls={`details-xs-${i}`}
                        >
                          {open[i] ? lessText : moreText}
                        </button>
                      </div>
                      <div
                        id={`details-xs-${i}`}
                        className={`${open[i] ? 'block' : 'hidden'} mt-3 text-[15px] leading-relaxed text-gray-700`}
                      >
                        {item.summary && <p className="mb-2">{item.summary}</p>}
                        {detailParagraphs.length > 0
                          ? detailParagraphs.map((paragraph, paragraphIndex) => (
                              <p
                                key={`${item.id ?? i}-mobile-${paragraphIndex}`}
                                className="mb-2 leading-relaxed"
                                dangerouslySetInnerHTML={{ __html: paragraph }}
                              />
                            ))
                          : item.details}
                      </div>
                    </>
                  )}
                </div>
              </div>

              <div
                className="hidden md:grid items-start"
                style={{
                  gridTemplateColumns: 'var(--tl-md) var(--date) var(--rail) minmax(0,1fr)',
                  columnGap: 'var(--gap)',
                }}
              >
                <div className="relative">
                  <NumberDot
                    index={i + 1}
                    topVar="calc(var(--pad-md) + var(--row-h)/2 - var(--dot)/2)"
                  />
                </div>

                <div className="col-[2/5] rounded-md bg-stone-50 p-6 border border-stone-200">
                  <div
                    className="grid items-start"
                    style={{
                      gridTemplateColumns: 'var(--date) minmax(0,1fr)',
                      columnGap: 'var(--gap)',
                    }}
                  >
                    <div className="flex flex-col items-start">
                      <div className="flex h-7 items-center">
                        {(item.announcementDate || item.date) && (
                          <span className="inline-flex h-7 items-center border-2 border-stone-200 shadow-lg whitespace-nowrap rounded-full bg-stone-900 text-white pl-2 pr-3 text-xs font-semibold">
                            <Calendar aria-hidden className="mr-1.5 h-3.5 w-3.5 opacity-80" />
                            {item.announcementDate || item.date}
                          </span>
                        )}
                      </div>

                      <div className="mt-2">
                        <AmountPill value={item.amount} />
                      </div>
                      {item.round && (
                        <div className="mt-2">
                          <RoundBadge value={item.round} />
                        </div>
                      )}
                      {Array.isArray(item.industries) && item.industries.length > 0 && (
                        <div className="mt-2 flex flex-wrap gap-2">
                          {item.industries.map((ind, idx) => (
                            <IndustryBadge key={idx} value={ind} />
                          ))}
                        </div>
                      )}
                    </div>

                    <div
                      className="grid"
                      style={{ gridTemplateColumns: 'minmax(0,1fr) var(--btn)', columnGap: 'var(--gap)' }}
                    >
                      <h3 className="min-w-0 break-words whitespace-normal text-xl font-semibold leading-7 text-gray-900 col-[1/2] h-7 flex items-center">
                        {item.title}
                      </h3>
                      {(item.hasRecipientCompanyInfo || item.recipientCompany || item.recipientCompanyUrl) && (
                        <div className="mt-2 col-[1/2]">
                          <DealRecipientCompany
                            companyName={item.recipientCompany}
                            companyUrl={item.recipientCompanyUrl}
                            hasInfo={item.hasRecipientCompanyInfo}
                            accessMode={recipientAccess}
                          />
                        </div>
                      )}

                      {hasDetails && (
                        <button
                          type="button"
                          onClick={() => toggle(i)}
                          className="h-7 w-full shrink-0 whitespace-nowrap cursor-pointer inline-flex items-center justify-center rounded-lg border px-3 text-sm font-medium border-gray-300 text-gray-700 hover:bg-gray-50 col-[2/3]"
                          aria-expanded={!!open[i]}
                          aria-controls={`details-${i}`}
                        >
                          {open[i] ? lessText : moreText}
                        </button>
                      )}

                      <div className="mt-4 col-[1/2]">
                        <div className="text-xs font-medium text-gray-500 uppercase tracking-wide">
                          Lead investors
                        </div>
                        <div className="mt-4 flex flex-wrap gap-2">
                          {(item.leadInvestors || []).map((node, idx) => (
                            <span key={idx} className="inline-flex items-center rounded-full border px-2.5 py-0.5 text-xs font-medium border-gray-300 text-gray-700">
                              {node}
                            </span>
                          ))}
                        </div>
                      </div>

                      {item.otherInvestors?.length > 0 && (
                        <div className="mt-4 col-[1/2]">
                          <div className="text-xs font-medium text-gray-500 uppercase tracking-wide">
                            Investors participated
                          </div>
                          <div className="mt-4 flex flex-wrap gap-2">
                            {item.otherInvestors.map((node, idx) => (
                              <span key={idx} className="inline-flex items-center rounded-full border px-2.5 py-0.5 text-xs font-medium border-gray-300 text-gray-700">
                                {node}
                              </span>
                            ))}
                          </div>
                        </div>
                      )}

                      {hasDetails && (
                        <div
                          id={`details-${i}`}
                          className={`${open[i] ? 'block' : 'hidden'} mt-4 text-[15px] leading-relaxed text-gray-700 col-[1/2]`}
                        >
                          {item.summary && <p className="mb-2">{item.summary}</p>}
                          {detailParagraphs.length > 0
                            ? detailParagraphs.map((paragraph, paragraphIndex) => (
                                <p
                                  key={`${item.id ?? i}-desktop-${paragraphIndex}`}
                                  className="mb-2 leading-relaxed"
                                  dangerouslySetInnerHTML={{ __html: paragraph }}
                                />
                              ))
                            : item.details}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </li>
          );
        })}
      </ol>
    </div>
  );
}
